import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "network-audit-mapping",
  kind: "horizontal",
  title: "Network Audit & Physical Mapping Systems",
  shortTitle: "Network Audits",
  status: "draft",
  tier: "experience",
  seoTitle: "Network Audit & Physical Mapping Systems | GA",
  seoDescription:
    "Physical network audits, cable tracing, logical topology modeling, and hyperlinked baseline diagrams across data centers, campuses, and industrial facilities.",
  summary:
    "Physical walkdowns, port-level hardware discovery, logical topology modeling, and interactive baseline diagrams that turn undocumented network sprawl into authoritative maps.",
  image: "/images/blog/mapping-the-undocumented-network-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: ["energy", "enterprise", "military", "healthcare"],
  relatedPostSlugs: [
    "mapping-the-undocumented-network",
    "reconciling-overlapping-security-frameworks",
    "air-gap-is-a-myth",
  ],
  engineering: [
    "Safe, systematic physical network audits and cable tracing across enterprise campuses, data centers, and active industrial plants",
    "Port-to-port physical hardware discovery reconciling patch panels, wall jacks, unmanaged switches, and field cabinets",
    "Logical L2/L3 topology reconstruction: VLAN segmentation, routing protocols, subnet allocations, and gateway configurations",
    "Comprehensive asset inventory reconciliation matching physical MAC addresses, serial numbers, and switch ports to logical hosts",
    "Interactive, hyperlinked network topology diagrams linking visual components directly to asset inventory databases",
    "Perimeter boundary auditing, rogue access point sweeps, and firewall interface verification",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "You Cannot Secure or Troubleshoot What You Have Not Mapped",
    },
    {
      type: "paragraph",
      text: "Whether across corporate campuses, hospital networks, enterprise data centers, or generating plants, networks accumulate decades of undocumented changes. Emergency jumper cables become permanent wiring, unmanaged switches hide beneath desks or inside control cabinets, and orphaned subnets linger indefinitely. Operating without an authoritative, physical-to-logical network map turns routine troubleshooting into days of downtime and leaves security teams blind to perimeter breaches.",
    },
    {
      type: "heading",
      level: 2,
      text: "Physical Walkdowns and Port-Level Hardware Discovery",
    },
    {
      type: "paragraph",
      text: "Automated network ping scanners only reveal devices currently responding to IP traffic—they miss passive taps, misconfigured interfaces, disconnected failover paths, and dark fiber runs. We perform methodical, hands-on physical walkdowns: tracing structured cabling, auditing rack elevations, cataloging switch port assignments, and identifying unauthorized hardware. Every physical connection is reconciled against observed network traffic.",
    },
    {
      type: "heading",
      level: 2,
      text: "Interactive, Living Topology Diagrams",
    },
    {
      type: "paragraph",
      text: "Static Visio or PDF diagrams quickly become obsolete shelfware. We deliver structured, hyperlinked network maps where clicking any switch, firewall, or server immediately exposes its port assignments, VLAN memberships, upstream uplink paths, and physical cabinet locations. The resulting topology models serve as living operational baselines for IT/OT engineering, audit compliance, and disaster recovery.",
    },
  ],
  phases: {
    study: {
      focus:
        "Define the audit scope, review available existing diagrams, establish physical access protocols, and deploy non-disruptive discovery tooling.",
      produces: [
        "Audit charter and site access coordination schedule",
        "Legacy diagram and IP address space gap assessment",
        "Passive listening and discovery tool deployment plan",
        "Cabinet, rack, and IDF/MDF physical walkdown checklist",
      ],
      fit: {
        good: [
          "Organizations with incomplete, outdated, or inherited network documentation across offices, data centers, or plant facilities",
          "Facilities preparing for regulatory compliance audits (NERC CIP, HIPAA, SOC 2, ISO 27001) requiring verified physical and logical baselines",
        ],
        poor: [
          "Organizations seeking automated remote vulnerability scanning without on-site physical verification",
          "Facilities where physical access to network closets, racks, or control cabinets is prohibited",
        ],
      },
    },
    "initial-design": {
      focus:
        "Conduct physical cabinet audits, trace structured cabling runs, and reconstruct the logical Layer 2 and Layer 3 architectures.",
      produces: [
        "Port-level physical connection schedules and patch panel run sheets",
        "Logical VLAN, subnet, and routing protocol architecture models",
        "Discovered unauthorized devices and rogue link exception reports",
        "Standardized diagram symbology and documentation taxonomy specification",
      ],
    },
    "detailed-design": {
      focus:
        "Draft authoritative multi-layer network diagrams and link visual elements to underlying asset databases.",
      produces: [
        "Interactive, hyperlinked master physical and logical network diagrams",
        "Switch port allocation tables and IDF/MDF cabinet rack elevations",
        "Perimeter firewall and gateway interface mapping documentation",
        "VLAN segmentation and inter-VLAN routing matrix",
      ],
    },
    implementation: {
      focus:
        "Publish finalized diagrams to operations repositories, conduct engineering walkdowns, and institute change management protocols.",
      produces: [
        "Complete baseline network audit documentation package",
        "Operational diagram maintenance and change-management procedure",
        "Labeling and physical cable tag cleanup in network cabinets",
        "Operations and network administration staff review and handover session",
      ],
    },
  },
};
